import { useEffect } from 'react';
import { CreditCard, Loader2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MoneyValue } from '../MoneyValue';
import { useCashlessSocket } from '@/hooks/useCashlessSocket';
import { cn } from '@/lib/utils';
import type { SocketConfig } from '@/types/sale';

interface EmoneyPanelProps {
  total: number;
  socketConfig: SocketConfig | null;
  /** Called when the reader reports success — chain into print. */
  onSuccess: () => void;
  onCancel: () => void;
  onRetry: () => void;
}

/** E-money reader tap flow driven by useCashlessSocket state machine. */
export function EmoneyPanel({
  total,
  socketConfig,
  onSuccess,
  onCancel,
  onRetry,
}: EmoneyPanelProps) {
  const { state, cancel } = useCashlessSocket({ config: socketConfig, open: Boolean(socketConfig) });

  useEffect(() => {
    if (state.phase === 'success') {
      const t = setTimeout(onSuccess, 900); // let the gold pulse breathe
      return () => clearTimeout(t);
    }
  }, [state.phase, onSuccess]);

  return (
    <div className="flex flex-col items-center gap-5 py-4 text-center">
      <div className="rounded-lg bg-secondary px-6 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-600">Total tagihan</p>
        <MoneyValue value={total} emphasis className="text-3xl" />
      </div>

      {(state.phase === 'connecting' || state.phase === 'waiting') && (
        <div className="flex flex-col items-center gap-3">
          <div className="relative flex h-28 w-28 items-center justify-center rounded-full bg-primary/10">
            <span className="absolute inset-0 animate-ping rounded-full bg-primary/15" />
            <CreditCard className="h-12 w-12 text-primary" />
          </div>
          <p className="font-display text-lg font-semibold text-ink-900">
            {state.phase === 'connecting' ? 'Menghubungkan pembaca…' : 'Tempelkan kartu e-money'}
          </p>
          <p className="text-sm text-muted-foreground">Tahan kartu pada pembaca hingga terdeteksi.</p>
        </div>
      )}

      {state.phase === 'insufficient' && (
        <div className="flex flex-col items-center gap-2">
          <AlertTriangle className="h-14 w-14 text-warning" />
          <p className="font-display text-lg font-semibold text-warning">Saldo tidak cukup</p>
          {typeof state.cardBalance === 'number' && (
            <p className="text-sm text-ink-600">
              Sisa saldo kartu: <MoneyValue value={state.cardBalance} className="text-sm" />
            </p>
          )}
          <p className="text-sm text-muted-foreground">Gunakan kartu lain atau batalkan.</p>
        </div>
      )}

      {state.phase === 'success' && (
        <div className="flex animate-gold-pulse flex-col items-center gap-2 rounded-full p-4">
          <CheckCircle2 className="h-16 w-16 text-success" />
          <p className="font-display text-xl font-bold text-success">Pembayaran berhasil</p>
          {typeof state.cardBalance === 'number' && (
            <p className="text-sm text-ink-600">
              Sisa saldo: <MoneyValue value={state.cardBalance} className="text-sm" />
            </p>
          )}
        </div>
      )}

      {(state.phase === 'canceled' || state.phase === 'error') && (
        <div className="flex flex-col items-center gap-2">
          <XCircle className="h-14 w-14 text-destructive" />
          <p className="font-display text-lg font-semibold text-destructive">
            {state.phase === 'error' ? (state.message ?? 'Terjadi kesalahan') : 'Transaksi dibatalkan'}
          </p>
        </div>
      )}

      <div className="flex w-full gap-3 pt-2">
        {(state.phase === 'waiting' || state.phase === 'insufficient' || state.phase === 'connecting') && (
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => {
              cancel();
              onCancel();
            }}
          >
            Batalkan
          </Button>
        )}
        {(state.phase === 'canceled' || state.phase === 'error') && (
          <>
            <Button variant="outline" className="flex-1" onClick={onCancel}>
              Ganti metode
            </Button>
            <Button className="flex-1" onClick={onRetry}>
              Coba lagi
            </Button>
          </>
        )}
        {state.phase === 'success' && (
          <Button variant="gold" className={cn('flex-1 gap-2')} onClick={onSuccess}>
            <Loader2 className="h-4 w-4 animate-spin" /> Menyiapkan cetak…
          </Button>
        )}
      </div>
    </div>
  );
}
